import { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Box, TextField, Typography, Paper, CircularProgress,
    Alert, Stack, Link
} from '@mui/material';
import * as api from './currenciesApi';
import type { Currency } from './currenciesApi';
import { sx14, paperSx, FormRow, DetailHeader } from '../common/detailView';

interface CurrencyViewProps {
    currencyId: number | null;
}

export function CurrencyView({ currencyId }: CurrencyViewProps) {
    const { t } = useTranslation();

    const [currency, setCurrency] = useState<Currency | null>(null);
    const [isEditing, setIsEditing] = useState(false);
    const [isNew, setIsNew] = useState(false);
    const [currencyCopy, setCurrencyCopy] = useState<Currency | null>(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const loadData = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            if (currencyId === null) {
                const empty: Currency = {
                    id: 0,
                    code: '',
                    name: '',
                    number: '',
                    currencyRate: 0,
                };
                setCurrency(empty);
                setIsNew(true);
                setIsEditing(true);
            } else {
                const data = await api.getCurrency(currencyId);
                setCurrency(data);
                setIsNew(false);
                setIsEditing(false);
            }
        } catch (err) {
            setError(String(err));
        } finally {
            setLoading(false);
        }
    }, [currencyId]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    if (loading) {
        return <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>;
    }

    if (error && !currency) {
        return <Alert severity="error">{error}</Alert>;
    }

    if (!currency) return null;

    const hasCode = (): boolean => !!currency.code && currency.code.length > 0;
    const hasName = (): boolean => !!currency.name && currency.name.length > 0;
    const hasNumber = (): boolean => !!currency.number && currency.number.length === 3;
    const hasRate = (): boolean => currency.currencyRate != null && !isNaN(Number(currency.currencyRate));
    const isAllValid = (): boolean => hasCode() && hasName() && hasNumber() && hasRate();

    const startEdit = () => {
        setCurrencyCopy(JSON.parse(JSON.stringify(currency)));
        setIsEditing(true);
    };

    const cancelEdit = () => {
        if (currencyCopy) setCurrency(currencyCopy);
        setIsEditing(false);
    };

    const save = async () => {
        setSaving(true);
        setError(null);
        try {
            const response = await api.saveCurrency(currency);
            setCurrency({ ...currency, id: response.id });
            setIsEditing(false);
            setIsNew(false);
        } catch (err) {
            setError(String(err));
        } finally {
            setSaving(false);
        }
    };

    const deleteCurrency = async () => {
        if (!window.confirm(t('currency.confirmDelete'))) return;
        setSaving(true);
        setError(null);
        try {
            await api.deleteCurrency(currency.id);
            window.history.back();
        } catch (err) {
            setError(String(err));
        } finally {
            setSaving(false);
        }
    };

    const updateField = <K extends keyof Currency>(field: K, value: Currency[K]) => {
        setCurrency({ ...currency, [field]: value });
    };

    return (
        <Paper variant="outlined" sx={paperSx}>
            <DetailHeader
                title={`${currency.code} ${currency.name}`}
                isEditing={isEditing}
                isNew={isNew}
                saving={saving}
                canSave={isAllValid()}
                onEdit={startEdit}
                onSave={save}
                onCancel={cancelEdit}
                onDelete={deleteCurrency}
            />

            <Box p={2}>
                <Stack spacing={2}>
                    {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}

                    <FormRow label={t('currency.code')}>
                        {!isEditing ? (
                            <Typography sx={sx14}>{currency.code}</Typography>
                        ) : (
                            <TextField size="small" fullWidth value={currency.code}
                                onChange={e => updateField('code', e.target.value)}
                                error={!hasCode()} />
                        )}
                    </FormRow>

                    <FormRow label={t('currency.name')}>
                        {!isEditing ? (
                            <Typography sx={sx14}>{currency.name}</Typography>
                        ) : (
                            <TextField size="small" fullWidth value={currency.name}
                                onChange={e => updateField('name', e.target.value)}
                                error={!hasName()} />
                        )}
                    </FormRow>

                    <FormRow label={t('currency.number')}>
                        {!isEditing ? (
                            <Typography sx={sx14}>{currency.number}</Typography>
                        ) : (
                            <TextField size="small" fullWidth placeholder="000"
                                inputProps={{ maxLength: 3 }}
                                value={currency.number}
                                onChange={e => updateField('number', e.target.value.replace(/[^0-9]/g, ''))}
                                error={!hasNumber()}
                                helperText={!hasNumber() ? `${t('common.required')} 3${t('common.piecesAbbr')}` : undefined} />
                        )}
                    </FormRow>

                    <FormRow label={t('currency.currencyRate')}>
                        {!isEditing ? (
                            <Typography sx={sx14}>{currency.currencyRate}</Typography>
                        ) : (
                            <TextField size="small" type="number" fullWidth
                                inputProps={{ step: 'any' }}
                                value={currency.currencyRate ?? ''}
                                onChange={e => updateField('currencyRate', e.target.value === '' ? 0 : Number(e.target.value))}
                                error={!hasRate()} />
                        )}
                    </FormRow>

                    <Box pt={2}>
                        <Link href="https://sv.wikipedia.org/wiki/ISO_4217" target="_blank" rel="noreferrer" sx={{ fontSize: '16px', color: '#337ab7', textDecoration: 'underline' }}>
                            {t('role.currency')} <span style={{ textTransform: 'lowercase' }}>{t('currency.code')}/{t('currency.number')} {t('common.information')}:</span> ISO 4217
                        </Link>
                    </Box>
                </Stack>
            </Box>

            {saving && <Box display="flex" justifyContent="center" p={1}><CircularProgress size={20} /></Box>}
        </Paper>
    );
}
